import React from "react";
import { Steps, Button, Spin, Alert, Drawer, Table, Tooltip } from "antd";
import { AppstoreOutlined, FileAddOutlined, HistoryOutlined, EyeOutlined, ApiOutlined } from "@ant-design/icons";
import PageHeader from "../../components/generic/PageHeader";
import { useGatewaySystem } from "./hooks/useGatewaySystem";
import { useGatewaySource } from "./hooks/useGatewaySource";
import { useGatewayJob }    from "./hooks/useGatewayJob";
import SystemSelector       from "./steps/SystemSelector";
import { FieldPreviewDrawer } from "./steps/SpecSelector";
import SourcePanel          from "./steps/SourcePanel";
import ColumnMapper         from "./steps/ColumnMapper";
import PreflightPanel       from "./steps/PreflightPanel";
import GeneratePanel        from "./steps/GeneratePanel";
import { NAVY, FLOW_STEPS, HISTORY_COLUMNS } from "./gateway.constants";
import "../../styles/gateway.css";

function GatewayPage() {
  const sys  = useGatewaySystem();
  const src  = useGatewaySource();
  const job  = useGatewayJob();

  const [step, setStep] = React.useState(0);

  // ── Handlers that bridge hooks ────────────────────────────────
  const handleSelectTemplate = (t) =>
    sys.onSelectTemplate(t, src.sourceColumns, src.autoSuggest);

  const handleSpecUploaded = (file) =>
    sys.onSpecUploaded(file, handleSelectTemplate);

  const handleSourceConfirmed = () =>
    src.onSourceConfirmed(sys.fields, () => setStep(1));

  const handleRunPreflight = () => {
    const req = job.buildRequest(
      sys.selectedTemplate, sys.email, src.sourceType,
      src.dbName, src.sql, src.columnMappings, src.groupByColumn,
    );
    job.onRunPreflight(req, src.uploadedFile, () => setStep(2));
  };

  const handleGenerate = (sftpId, saveName) => {
    const req = job.buildRequest(
      sys.selectedTemplate, sys.email, src.sourceType,
      src.dbName, src.sql, src.columnMappings, src.groupByColumn,
    );
    job.onGenerate(req, src.uploadedFile, sftpId, saveName, () => setStep(3));
  };

  const handleDownload = () => job.onDownload(job.jobId);

  const reset = () => {
    setStep(0);
    sys.setSelectedTemplate(null);
    src.resetSource();
    job.resetJob();
  };

  // ── Step content registry ─────────────────────────────────────
  const STEP_CONTENT = {
    0: sys.selectedTemplate ? (
      <SourcePanel
        sourceType={src.sourceType}     setSourceType={src.setSourceType}
        uploadedFile={src.uploadedFile} setUploadedFile={src.setUploadedFile}
        dbName={src.dbName}             setDbName={src.setDbName}
        sql={src.sql}                   setSql={src.setSql}
        onNext={handleSourceConfirmed}
        onBack={() => sys.setSelectedTemplate(null)}
      />
    ) : null,
    1: (
      <ColumnMapper
        fields={sys.fields}
        sourceColumns={src.sourceColumns}
        mappings={src.columnMappings}
        savedMappings={sys.savedMappings}
        systemId={sys.selectedSystem?.systemId}
        hasRepeatingGroups={sys.fields.some((f) => f.repeatingGroupKey)}
        groupByColumn={src.groupByColumn}
        onGroupByColumnChange={src.setGroupByColumn}
        onChange={src.setColumnMappings}
        onLoadSaved={(s) => src.setColumnMappings(s.columnMappings)}
        onNext={handleRunPreflight}
        onBack={() => setStep(0)}
        preflightLoading={job.preflightLoading}
      />
    ),
    2: (
      <PreflightPanel
        report={job.preflightReport}
        onProceed={() => setStep(3)}
        onFix={() => setStep(1)}
        onBack={() => setStep(1)}
      />
    ),
    3: (
      <GeneratePanel
        fields={sys.fields}
        mappings={src.columnMappings}
        sftpDests={sys.sftpDests}
        generating={job.generating}
        progress={job.progress}
        jobStatus={job.jobStatus}
        jobResult={job.jobResult}
        preflightReport={job.preflightReport}
        onGenerate={handleGenerate}
        onDownload={handleDownload}
        onBack={() => setStep(2)}
        onReset={reset}
      />
    ),
  };

  if (sys.loading)
    return <div style={{ padding: 80, textAlign: "center" }}><Spin size="large" /></div>;

  return (
    <div style={{ padding: "16px 24px", minHeight: "100vh" }}>
      <PageHeader
        icon={<ApiOutlined />}
        title="ARC Gateway"
        tooltip="Data Interface Generator — build import files for target financial systems"
        actions={
          <Button
            icon={<HistoryOutlined />}
            size="small"
            onClick={() => job.loadHistory(sys.email)}
          >
            Run history
          </Button>
        }
      />

      {systems_empty(sys) ? (
        <Alert type="info" showIcon message="No systems available"
          description="Contact your ARC administrator to get access to target systems."
          style={{ maxWidth: 480, margin: "60px auto" }} />
      ) : (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          {/* Left panel — system + format library */}
          <SystemSelector
            systems={sys.systems}
            selectedSystem={sys.selectedSystem}
            onSelectSystem={sys.onSelectSystem}
            templates={sys.templates}
            selectedTemplate={sys.selectedTemplate}
            loadingTemplates={sys.loadingTemplates}
            onSelectTemplate={handleSelectTemplate}
            onPreviewTemplate={sys.onPreviewTemplate}
            onUploadSpec={handleSpecUploaded}
          />

          {/* Right panel — wizard */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {!sys.selectedSystem ? (
              <EmptyState icon={<AppstoreOutlined />} title="Select a target system" sub="Choose from the panel on the left" />
            ) : !sys.selectedTemplate ? (
              <EmptyState icon={<FileAddOutlined />} title="Choose a format from the library" sub="Select an existing spec or upload a new one" />
            ) : (
              <div style={{
                background: "var(--color-background-primary)",
                border: "0.5px solid var(--color-border-tertiary)",
                borderRadius: 12, padding: "20px 24px",
              }}>
                {/* Selected format banner */}
                <TemplateBanner
                  system={sys.selectedSystem}
                  template={sys.selectedTemplate}
                  fieldCount={sys.fields.length}
                  onPreview={() => sys.onPreviewTemplate(sys.selectedTemplate)}
                  onClear={() => { sys.setSelectedTemplate(null); setStep(0); }}
                />

                {/* Step progress bar */}
                <Steps current={step} size="small" style={{ marginBottom: 24 }}
                  items={FLOW_STEPS.map((s, i) => ({
                    title: s.title,
                    description: <span style={{ fontSize: 10 }}>{s.desc}</span>,
                    status: i < step ? "finish" : i === step ? "process" : "wait",
                  }))}
                />

                {STEP_CONTENT[step]}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Field preview drawer */}
      <FieldPreviewDrawer
        open={sys.previewOpen}
        template={sys.previewTemplate}
        fields={sys.previewFields}
        onClose={() => sys.setPreviewOpen(false)}
        onUse={handleSelectTemplate}
      />

      {/* History drawer */}
      <Drawer title="Generation history" width={960}
        open={job.showHistory} onClose={() => job.setShowHistory(false)}>
        <Table size="small" dataSource={job.history} rowKey="job_id"
          pagination={{ pageSize: 20 }} columns={HISTORY_COLUMNS} />
      </Drawer>
    </div>
  );
}

// ── Small local helpers (too small to extract to files) ───────

function systems_empty(sys) {
  return !sys.loading && sys.systems.length === 0;
}

function EmptyState({ icon, title, sub }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", height: 380,
      color: "var(--color-text-secondary)",
      border: "1px dashed var(--color-border-tertiary)", borderRadius: 12,
    }}>
      {React.cloneElement(icon, { style: { fontSize: 44, marginBottom: 16, opacity: 0.25 } })}
      <div style={{ fontSize: 15, fontWeight: 500 }}>{title}</div>
      <div style={{ fontSize: 12, marginTop: 6, opacity: 0.6 }}>{sub}</div>
    </div>
  );
}

function TemplateBanner({ system, template, fieldCount, onPreview, onClear }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12, marginBottom: 20,
      padding: "10px 14px", borderRadius: 8,
      background: `${system.accentColor || NAVY}0a`,
      border: `1px solid ${system.accentColor || NAVY}22`,
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: 6, flexShrink: 0,
        background: system.accentColor || NAVY, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontWeight: 700, fontSize: 10, fontFamily: "monospace",
      }}>
        {system.logoInitials}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {system.displayName} — {template.templateName}
        </div>
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
          {template.dataType} · {template.formatTypeId?.replace("_", " ")} · {fieldCount} fields
        </div>
      </div>
      <Button size="small" type="link" icon={<EyeOutlined />} onClick={onPreview}>
        Preview spec
      </Button>
      <Button size="small" type="link" onClick={onClear}>Change</Button>
    </div>
  );
}

export default GatewayPage;
